/**
 * Layout for reading logs: a table of dated entries whose columns the reader chooses, an
 * optional fill-in-on-screen mode, and a totals row. Positions are millimetres on a page
 * from `pageGeometry`; rows paginate with `layoutTable` from the attendance layout.
 */

import { parseNumberInput } from "@/lib/parse-number";
import { type Col, splitColumns } from "@/lib/tool-kit/printables/attendance";
import type { PageGeometry } from "@/lib/tool-kit/printables/page";

export const LOG_COLUMNS = [
  "date",
  "title",
  "author",
  "pages",
  "minutes",
  "rating",
  "response",
  "initials",
] as const;

export type LogColumn = (typeof LOG_COLUMNS)[number];

/** Relative widths; each is scaled so the chosen columns fill the page. */
export const COLUMN_WEIGHT: Record<LogColumn, number> = {
  date: 13,
  title: 28,
  author: 20,
  pages: 11,
  minutes: 11,
  rating: 18,
  response: 42,
  initials: 13,
};

export const ROW_HEIGHTS = { compact: 7, standard: 9, roomy: 12 } as const;
export type RowSize = keyof typeof ROW_HEIGHTS;

/** Ready-made column sets. */
export const LOG_PRESETS: Record<
  "nightly" | "book" | "response",
  readonly LogColumn[]
> = {
  nightly: ["date", "title", "pages", "minutes", "initials"],
  book: ["date", "title", "author", "pages", "rating"],
  response: ["date", "title", "pages", "response", "rating"],
};

export interface LogColumnLayout {
  key: LogColumn;
  col: Col;
}

/** The chosen columns in the standard order, filling the content width. */
export function logColumns(
  geometry: PageGeometry,
  enabled: ReadonlySet<LogColumn>
): LogColumnLayout[] {
  const keys = LOG_COLUMNS.filter((key) => enabled.has(key));
  const total = keys.reduce((sum, key) => sum + COLUMN_WEIGHT[key], 0) || 1;
  const widths = keys.map(
    (key) => (geometry.contentWidth * COLUMN_WEIGHT[key]) / total
  );
  const columns = splitColumns(geometry.left, widths);
  return keys.map((key, index) => ({
    key,
    col: columns[index] ?? { x: geometry.left, width: widths[index] ?? 0 },
  }));
}

/** Row height: the chosen one, but at least 10 mm when there is a response column. */
export function rowHeightFor(size: RowSize, hasResponse: boolean): number {
  const height = ROW_HEIGHTS[size];
  return hasResponse ? Math.max(height, 10) : height;
}

export const MAX_LOG_ROWS = 60;

/** Entries typed on screen, by row. Cells are free text, except rating (0–5). */
export type LogEntries = Record<number, Partial<Record<LogColumn, string>>>;

/** Sums the numbers in a column across the first `rows` rows; non-numbers count as zero. */
export function columnTotal(
  entries: LogEntries,
  key: "pages" | "minutes",
  rows: number
): number | null {
  let total = 0;
  let found = false;
  for (let row = 0; row < rows; row += 1) {
    const value = parseNumberInput(entries[row]?.[key] ?? "");
    if (value !== null) {
      total += value;
      found = true;
    }
  }
  return found ? Math.round(total * 100) / 100 : null;
}

/** A rating cell's stars, 0–5. */
export function ratingOf(text: string | undefined): number {
  const value = Number(text);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(5, Math.max(0, Math.round(value)));
}
